import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { AlertTriangle, PauseCircle, WifiOff, XOctagon } from "lucide-react";
import { useModules } from "../../hooks/useModules";
import type { IntakeConnectionHealth } from "../../types/containerOrderIntake";

/**
 * How long the line may be quiet before silence is itself a fault.
 *
 * Polling is once a minute, so fifteen is fifteen consecutive misses — well
 * past a slow pass or a restart, well short of a working day. It catches what
 * no status field can: a scheduler that never started after a deploy, or a
 * pass wedged on a request that will not time out. Those leave the connection
 * reading `connected`, forever, with nothing coming in.
 */
const SILENT_AFTER_MS = 15 * 60 * 1000;

type Tone = "red" | "amber" | "grey";

const TONE: Record<Tone, string> = {
  red: "bg-red-50 border-red-200 text-red-900",
  amber: "bg-amber-50 border-amber-200 text-amber-900",
  grey: "bg-[#F5F4F0] border-[#E5E3DC] text-[#4A5550]",
};

const ICON_TONE: Record<Tone, string> = {
  red: "text-red-600",
  amber: "text-amber-600",
  grey: "text-[#8A9590]",
};

const fmtDateTime = (d?: string | null) =>
  d ? new Date(d).toLocaleString("et-EE", { dateStyle: "short", timeStyle: "short" }) : null;

/**
 * What the connection behind this queue is doing — for admins only.
 *
 * An empty queue reads as "a quiet morning" whether or not anything is still
 * arriving, and the people who live on this page are not the people who watch
 * Settings → Integrations. This is the one line that tells them apart. The
 * connection itself is never sent to anyone but an admin, so a null here means
 * either "nothing to say" or "not yours to see", and both render as nothing.
 */
const IntakeConnectionBanner: React.FC<{ connection: IntakeConnectionHealth | null }> = ({
  connection,
}) => {
  const { t } = useTranslation("containerOrderIntake");
  const { has } = useModules();
  if (!connection) return null;

  // Measured from the last time Oliver answered — or, if it never has, from
  // when the connection was saved: a token entered a minute ago has not gone
  // quiet, one entered last week and never answered has.
  const since = connection.lastSuccessAt || connection.createdAt;
  const silentFor = since ? Date.now() - new Date(since).getTime() : 0;

  // In the order a reader should hear them: what is certainly broken, what was
  // chosen, what may yet right itself, and last what is merely suspicious.
  const state = connection.authFailed
    ? "refused"
    : !connection.enabled
      ? "off"
      : connection.status === "error"
        ? "unreachable"
        : silentFor > SILENT_AFTER_MS
          ? "silent"
          : null;

  if (!state) return null;

  const tone: Tone =
    state === "refused" ? "red" : state === "off" ? "grey" : "amber";

  const Icon =
    state === "refused"
      ? XOctagon
      : state === "off"
        ? PauseCircle
        : state === "unreachable"
          ? WifiOff
          : AlertTriangle;

  const lastSuccess = fmtDateTime(connection.lastSuccessAt);
  const lastTried = fmtDateTime(connection.lastPolledAt);

  return (
    <div className={`flex flex-wrap items-start gap-3 rounded-md border px-3 py-2.5 text-[13px] ${TONE[tone]}`}>
      <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${ICON_TONE[tone]}`} />

      <div className="min-w-0 flex-1 space-y-0.5">
        <div className="font-medium">{t(`health.title.${state}`)}</div>
        <div>{t(`health.${state}`)}</div>

        {/* Oliver's own words, which are the ones worth quoting to Oliver. */}
        {connection.lastError && state !== "off" && (
          <div className="break-words opacity-80">{connection.lastError}</div>
        )}

        <div className="text-[12px] opacity-75">
          {lastSuccess
            ? t("health.lastSuccess", { when: lastSuccess })
            : t("health.lastSuccessNever")}
          {lastTried && state !== "off" ? ` · ${t("health.lastTried", { when: lastTried })}` : ""}
        </div>
      </div>

      {/* Never a door the router will refuse: the integration page is its own
          module, and this banner outlives it being switched off. */}
      {has("integrations") && (
        <Link
          to="/integrations"
          className="shrink-0 rounded-md border border-current/30 bg-white/70 px-2.5 py-1 font-medium hover:bg-white"
        >
          {t("health.fix")}
        </Link>
      )}
    </div>
  );
};

export default IntakeConnectionBanner;
